import type { TenantTimePollSavePayload } from '@tark/shared/types';

export type TimePollSlotDraft = {
  key: string;
  startsAt: string;
  endsAt: string;
  locationLabel: string;
  capacity: string;
};

export type TimePollDraft = {
  programId: string;
  title: string;
  deadlineAt: string;
  athleteIds: number[];
  slots: TimePollSlotDraft[];
};

export function toLocalDateTime(date: Date): string {
  const offset = date.getTimezoneOffset() * 60_000;
  return new Date(date.getTime() - offset).toISOString().slice(0, 16);
}

export function createDefaultTimePollDraft(): TimePollDraft {
  const firstStart = new Date();
  firstStart.setDate(firstStart.getDate() + 1);
  firstStart.setHours(16, 0, 0, 0);
  const secondStart = new Date(firstStart);
  secondStart.setDate(secondStart.getDate() + 1);

  return {
    programId: '',
    title: '',
    deadlineAt: '',
    athleteIds: [],
    slots: [firstStart, secondStart].map((start, index) => ({
      key: `new-${index}`,
      startsAt: toLocalDateTime(start),
      endsAt: toLocalDateTime(new Date(start.getTime() + 60 * 60_000)),
      locationLabel: '',
      capacity: '',
    })),
  };
}

export function validateTimePollDraft(draft: TimePollDraft): string | null {
  if (!draft.programId) return 'Select a program.';
  if (!draft.title.trim()) return 'Enter a short poll title.';
  if (draft.athleteIds.length === 0) return 'Select at least one student.';
  if (draft.slots.length < 2 || draft.slots.length > 8) return 'Add between 2 and 8 time options.';

  for (const slot of draft.slots) {
    const startsAt = new Date(slot.startsAt);
    const endsAt = new Date(slot.endsAt);
    if (Number.isNaN(startsAt.getTime()) || Number.isNaN(endsAt.getTime()) || endsAt <= startsAt) {
      return 'Each time option needs a valid start and end time.';
    }
    if (slot.capacity) {
      const capacity = Number(slot.capacity);
      if (!Number.isInteger(capacity) || capacity < 1 || capacity > 999) {
        return 'Capacity must be between 1 and 999.';
      }
    }
  }

  if (draft.deadlineAt && Number.isNaN(new Date(draft.deadlineAt).getTime())) {
    return 'Choose a valid response deadline.';
  }
  return null;
}

export function buildTimePollPayload(draft: TimePollDraft, timezone: string): TenantTimePollSavePayload {
  return {
    programId: Number(draft.programId),
    title: draft.title.trim(),
    deadlineAt: draft.deadlineAt ? new Date(draft.deadlineAt).toISOString() : undefined,
    timezone,
    athleteIds: draft.athleteIds,
    slots: draft.slots.map((slot) => ({
      startsAt: new Date(slot.startsAt).toISOString(),
      endsAt: new Date(slot.endsAt).toISOString(),
      locationLabel: slot.locationLabel.trim() || undefined,
      capacity: slot.capacity ? Number(slot.capacity) : undefined,
    })),
  };
}

export function formatTimePollSlot(startsAt: string, endsAt: string): string {
  const start = new Date(startsAt);
  const end = new Date(endsAt);
  return `${start.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}, ${start.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })} - ${end.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}`;
}
